import { brandTrustedDomains, normalizeBrandDomain } from '@rock/brands';
import type { Brand } from '@rock/types';

/** One of the brand's owned domains, with where it came from. */
export interface BrandDomainRow {
    domain: string;
    /** Explicit and researched rows are removable; website/brandfetch rows follow their own fields on Details. */
    source: 'explicit' | 'researched' | 'website' | 'brandfetch';
}

export const BRAND_DOMAIN_SOURCE_LABEL: Record<BrandDomainRow['source'], string> = {
    explicit: 'Added here',
    researched: 'AI research',
    website: 'Website',
    brandfetch: 'Brandfetch',
};

/**
 * Whether a row can be REMOVED here — the two explicit lists an admin owns. The implicit website /
 * Brandfetch members follow their own fields on the Details tab instead.
 */
export function isRemovableDomainSource(source: BrandDomainRow['source']): boolean {
    return source === 'explicit' || source === 'researched';
}

/**
 * The brand's owned domains as labeled rows — the display form of `brandOwnedDomains`, which returns the
 * bare set with provenance already collapsed. Shared by the Domains tab and the Brandfetch dialog's
 * source picker so the two lists can't drift.
 *
 * Dedupe precedence and display order are deliberately opposite: a hostname is labeled by the first
 * group to claim it (explicit beats researched beats implicit — the field an admin can actually edit),
 * while the rows come back implicit-first so the brand's primary domains lead the list.
 */
export function buildBrandDomainRows(brand: Brand): BrandDomainRow[] {
    const explicit = (brand.domains ?? []).map((domain): BrandDomainRow => ({ domain, source: 'explicit' }));
    const seen = new Set(explicit.map((row) => row.domain));
    const researched: BrandDomainRow[] = [];
    for (const domain of brand.researchedDomains ?? []) {
        if (seen.has(domain)) continue;
        seen.add(domain);
        researched.push({ domain, source: 'researched' });
    }
    const implicit: BrandDomainRow[] = [];
    const websiteHost = brand.website ? normalizeBrandDomain(brand.website) : null;
    if (websiteHost && !seen.has(websiteHost)) {
        seen.add(websiteHost);
        implicit.push({ domain: websiteHost, source: 'website' });
    }
    const brandfetchHost = brand.brandfetch ? normalizeBrandDomain(brand.brandfetch.domain) : null;
    if (brandfetchHost && !seen.has(brandfetchHost)) {
        implicit.push({ domain: brandfetchHost, source: 'brandfetch' });
    }
    return [...implicit, ...researched, ...explicit];
}

/** Why an add of `input` to either domain list was refused, for the toast. */
export function domainRefusalMessage(brand: Brand, input: string): string {
    const domain = normalizeBrandDomain(input);
    if (!domain) return 'Not a usable domain. A hostname needs a dot, e.g. whirlpool.com.';
    if (brandTrustedDomains(brand).includes(domain)) return `“${domain}” is already a trusted domain of this brand.`;
    return `“${domain}” is already covered by this brand’s owned domains.`;
}
